import React from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingProps, DotsLoadingProps } from './Loading.types'

// Spinner Loading Component
const Loading: React.FC<LoadingProps> = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 border-2',
    md: 'w-12 h-12 border-3',
    lg: 'w-20 h-20 border-4',
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`${sizeClasses[size]} rounded-full border-r-transparent border-[var(--accent-color)] animate-spin`}
        role="status"
        aria-label="loading"
      />
    </div>
  )
}

// Dots Loading Component
export const DotsLoading: React.FC<DotsLoadingProps> = ({
  size = 'md',
  color = 'accent',
  className = '',
  text,
  showText = false,
}) => {
  const { t } = useTranslation()

  const sizeClasses = {
    sm: {
      dot: 'w-1.5 h-1.5',
      gap: 'gap-1',
      text: 'text-xs',
    },
    md: {
      dot: 'w-2 h-2',
      gap: 'gap-1.5',
      text: 'text-sm',
    },
    lg: {
      dot: 'w-3 h-3',
      gap: 'gap-2',
      text: 'text-base',
    },
  }

  const colorClasses = {
    primary: 'bg-[var(--loading-dots-primary)]',
    accent: 'bg-[var(--loading-dots-accent)]',
    secondary: 'bg-[var(--loading-dots-secondary)]',
  }

  const loadingText = text || t('common.loading', 'Loading...')

  return (
    <div
      className={`flex items-center justify-center ${className}`}
      role="status"
      aria-label={loadingText}
    >
      <div className="flex flex-col items-center gap-2">
        {/* Three Dots Animation */}
        <div className={`flex items-center ${sizeClasses[size].gap}`}>
          <div
            className={`${sizeClasses[size].dot} ${colorClasses[color]} rounded-full animate-bounce`}
            style={{
              animationDelay: '0ms',
              animationDuration: '1.0s',
              animationTimingFunction: 'ease-in-out',
            }}
          />
          <div
            className={`${sizeClasses[size].dot} ${colorClasses[color]} rounded-full animate-bounce`}
            style={{
              animationDelay: '120ms',
              animationDuration: '1.0s',
              animationTimingFunction: 'ease-in-out',
            }}
          />
          <div
            className={`${sizeClasses[size].dot} ${colorClasses[color]} rounded-full animate-bounce`}
            style={{
              animationDelay: '240ms',
              animationDuration: '1.0s',
              animationTimingFunction: 'ease-in-out',
            }}
          />
        </div>

        {/* Optional Loading Text */}
        {showText && (
          <span
            className={`${sizeClasses[size].text} text-[var(--loading-text)] font-medium`}
          >
            {loadingText}
          </span>
        )}
      </div>
    </div>
  )
}

export default Loading
